import api, { methods } from "@api";
import { toast } from "@provider/toast";
import type { Recipe, WishlistRecipe } from "@type/recipe";
import type { RecipeWishlists } from "@type/wishlist";
import { debounce, normalDebounce } from "@utils";
import { create } from "zustand";

interface RecipeWishlistState {
  recipeWishlist: RecipeWishlists;
  optimisticUpdates: boolean;
  initial: (data: RecipeWishlists) => void;
  load: () => void;
  addOrRemove: ({ recipe, userId }: { recipe: Recipe; userId: number }) => void;
  addWishlistToCart: () => void;
}

const getRecipeWishlistDebouncer = normalDebounce(() => useRecipeWishlistStore.getState().load(), 1200);
const updateWishlistDebounced = () => getRecipeWishlistDebouncer();

function addRecipeToWishlistDebounced({ recipeId, oldWishlist }: { recipeId: number; oldWishlist: WishlistRecipe[] }) {
  debounce(
    async () => {
      try {
        const res = await api<{ group: string; message: string }>({
          path: "/wishlist/recipe",
          method: methods.POST,
          body: { recipeId },
        });

        // toast.show(res.message);

        return res;
      } catch (err) {
        toast.show((err as any).message);
        useRecipeWishlistStore.setState((prev) => ({ ...prev, wishlist: oldWishlist }));
      }
    },
    `recipe_${recipeId}`,
    300
  );
}

export const useRecipeWishlistStore = create<RecipeWishlistState>((set, get) => ({
  recipeWishlist: [],
  optimisticUpdates: false,
  initial: (data) => {
    set({ recipeWishlist: data });
  },
  load: async () => {
    try {
      const response = await api<{ recipe_wishlists: WishlistRecipe[] }>({
        path: "/wishlist/recipe",
        method: methods.GET,
      });

      set({ recipeWishlist: response.recipe_wishlists, optimisticUpdates: false });
    } catch (e) {
      console.log("error", e);
      toast.show("fout bij laden van favorieten");
    }
  },
  addOrRemove: ({ recipe, userId }) => {
    const oldWishlist = get().recipeWishlist;

    const newWishlist = [...oldWishlist];
    const existingRecipe = newWishlist.findIndex((recp) => recp.recipe_id === recipe.id);
    if (existingRecipe >= 0) {
      newWishlist.splice(existingRecipe, 1);
    } else {
      newWishlist.push({ id: newWishlist.length + 1, recipe_id: recipe.id, user_id: userId, recipe });
    }

    set({ recipeWishlist: newWishlist, optimisticUpdates: true });

    addRecipeToWishlistDebounced({
      recipeId: recipe.id,
      oldWishlist,
    });

    updateWishlistDebounced();
  },
  addWishlistToCart: () => {},
}));

export function useWishlistRecipe({ recipeId }: { recipeId: number | undefined }) {
  return useRecipeWishlistStore((store) => store.recipeWishlist?.find((w) => w.recipe_id === recipeId));
}
